import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaPlus,
  FaCog,
  FaUnlink,
} from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useDebounce } from "../../../Hooks/useDebounce";
import {
  useGetEndUsersByFirmQuery,
  useSearchUnlinkedEndUsersMutation,
  useLinkEndUserToFirmMutation,
  useUnlinkEndUserFromFirmMutation,
  useUpdateEndUserQuotaMutation,
  useGetEmployeeTemplatesQuery,
} from "../../../store";
import { MascotLoadingOverlay, EmptyState, SearchInput } from "../../shared";
import EmployeeQuotaDrawer, { PERIOD_LABELS, formatTL } from "./EmployeeQuotaDrawer";
import ConfirmationModal from "../../../Components/Firm/Tabs/ConfirmationModal";
import { Alert } from "@mui/material";

// extraFilter: şablon sütunundan gelen süzgeç (#298).
const EmployeesTab = ({ firm, readOnly = false, extraFilter }) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const [search, setSearch] = useState("");
  const [alert, setAlert] = useState(null);

  // Dropdown states
  const [quotaDrawerUser, setQuotaDrawerUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownResults, setDropdownResults] = useState([]);
  const dropdownRef = useRef(null);

  // Confirmation modal states
  const [confirmLink, setConfirmLink] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const debouncedDropdownSearch = useDebounce(dropdownSearch, 400);

  const canRead = hasPermission("firms.employees");
  const canLink = !readOnly && hasPermission("firms.employeesLink");
  const canUnlink = !readOnly && hasPermission("firms.employeesUnlink");
  const canEditQuota = !readOnly && hasPermission("firms.employeesEditQuota");

  // Queries
  const { data: endUsersData, isLoading, isFetching, refetch } = useGetEndUsersByFirmQuery(
    { token: auth?.accessToken, firmId: firm?._id },
    // Şablon düzenlemesi bakiyeleri sunucuda değiştirir; sekmeye dönüşte taze çek
    { skip: !auth?.accessToken || !firm?._id || !canRead, refetchOnMountOrArgChange: true }
  );

  // Employee templates for the firm
  const { data: templatesData } = useGetEmployeeTemplatesQuery(
    { token: auth?.accessToken, id: firm?._id },
    { skip: !auth?.accessToken || !firm?._id || !canLink }
  );
  const employeeTemplates = templatesData?.data?.templates || [];

  const [searchUnlinked, { isLoading: searchLoading }] = useSearchUnlinkedEndUsersMutation();
  const [linkEndUser, { isLoading: linkLoading }] = useLinkEndUserToFirmMutation();
  const [unlinkEndUser, { isLoading: unlinkLoading }] = useUnlinkEndUserFromFirmMutation();
  const [updateQuota, { isLoading: updateLoading }] = useUpdateEndUserQuotaMutation();

  const endUsers = useMemo(() => endUsersData?.data || [], [endUsersData]);

  // Arama gecikmeli (#407) — her tuşta liste yeniden süzülüp çizilmesin.
  const debouncedSearch = useDebounce(search, 250);
  const filteredUsers = useMemo(() => {
    const s = debouncedSearch.toLowerCase();
    return endUsers.filter((user) => {
      if (extraFilter && !extraFilter(user)) return false;
      return (
        user.name?.toLowerCase().includes(s) ||
        user.lastName?.toLowerCase().includes(s) ||
        user.email?.toLowerCase().includes(s) ||
        user.tel?.toLowerCase().includes(s)
      );
    });
  }, [endUsers, extraFilter, debouncedSearch]);

  // Alert timeout
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Debounce search for dropdown
  useEffect(() => {
    if (!debouncedDropdownSearch || debouncedDropdownSearch.length < 2) {
      setDropdownResults([]);
      return;
    }
    const doSearch = async () => {
      try {
        const result = await searchUnlinked({
          token: auth.accessToken,
          search: debouncedDropdownSearch,
        }).unwrap();
        setDropdownResults(result.data || []);
      } catch {
        setDropdownResults([]);
      }
    };
    doSearch();
  }, [debouncedDropdownSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkUser = async (userId) => {
    if (!selectedTemplateId) {
      setAlert({ severity: "error", message: "Lütfen bir çalışan şablonu seçin!" });
      return;
    }
    try {
      await linkEndUser({
        token: auth.accessToken,
        userId,
        firmId: firm._id,
        employeeTemplateId: selectedTemplateId,
      }).unwrap();
      setAlert({ severity: "success", message: "Çalışan firmaya başarıyla bağlandı" });
      setDropdownResults((prev) => prev.filter((u) => u._id !== userId));
      setConfirmLink(null);
      setSelectedTemplateId("");
      refetch();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Bağlama sırasında hata oluştu" });
      setConfirmLink(null);
      setSelectedTemplateId("");
    }
  };

  const handleUnlinkUser = async (userId) => {
    try {
      await unlinkEndUser({ token: auth.accessToken, userId }).unwrap();
      setAlert({ severity: "success", message: "Çalışan firmadan çıkarıldı" });
      setConfirmUnlink(null);
      refetch();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Çıkarma sırasında hata oluştu" });
      setConfirmUnlink(null);
    }
  };

  const handleSaveQuota = async (data) => {
    try {
      await updateQuota({ token: auth.accessToken, ...data }).unwrap();
      setAlert({ severity: "success", message: "Kota ayarları güncellendi" });
      setQuotaDrawerUser(null);
      refetch();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Güncelleme sırasında hata oluştu" });
    }
  };

  // Dönem ve tutar çalışan şablonundan (tek kaynak, #362) — kullanıcının
  // periodOptions'ı mobil çıkışta pasifleştiği için kullanılmaz.
  const getPeriodSummary = (user) => {
    const t = user.employeeTemplateInfo;
    if (!t) return "—";
    return `${PERIOD_LABELS[t.periodType] || t.periodType} ${formatTL(t.periodQuota)}`;
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Alert */}
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "12px", fontSize: "13px" }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shrink-0 px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center shrink-0">
            <FaUser className="w-4 h-4 text-orange-500" />
          </div>
          <div className="shrink-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-800">Çalışanlar</h2>
            <p className="text-xs text-gray-400">{filteredUsers.length} çalışan kayıtlı</p>
          </div>
          <SearchInput
            placeholder="İsim, email veya telefon ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName="flex-1 min-w-0 ml-auto max-w-xs"
          />

          {/* Çalışan Ekle Dropdown */}
          {canLink && (
            <div className="relative shrink-0" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setDropdownSearch("");
                  setDropdownResults([]);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-xl text-xs font-semibold hover:bg-orange-600 transition-colors"
              >
                <FaPlus className="w-3 h-3" />
                <span className="hidden sm:inline">Çalışan Ekle</span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
                  <div className="p-3 border-b border-gray-100">
                    <input
                      type="text"
                      autoFocus
                      placeholder="İsim, email veya telefon ile arayın..."
                      value={dropdownSearch}
                      onChange={(e) => setDropdownSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    />
                  </div>

                  <div className="max-h-60 overflow-y-auto">
                    {searchLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : dropdownSearch.length < 2 ? (
                      <p className="text-xs text-gray-400 text-center py-6">
                        En az 2 karakter girin
                      </p>
                    ) : dropdownResults.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-6">
                        Sonuç bulunamadı
                      </p>
                    ) : (
                      <div className="py-1">
                        {dropdownResults.map((user) => (
                          <div
                            key={user._id}
                            className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-orange-50 cursor-pointer transition-colors"
                            onClick={() => setConfirmLink(user)}
                          >
                            <div className="w-7 h-7 bg-orange-50 rounded-full flex items-center justify-center shrink-0">
                              <FaUser className="w-2.5 h-2.5 text-orange-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {user.name} {user.lastName}
                              </p>
                              <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
                            </div>
                            <FaPlus className="w-3 h-3 text-orange-500 shrink-0" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading || isFetching ? (
          <div className="flex items-center justify-center py-16">
            <MascotLoadingOverlay message="Çalışanlar yükleniyor..." />
          </div>
        ) : filteredUsers.length === 0 ? (
          <EmptyState
            message="Çalışan Bulunamadı"
            suggestion={
              search
                ? "Arama kriterlerinizi değiştirmeyi deneyin."
                : extraFilter && endUsers.length > 0
                  ? "Bu şablonda çalışan yok."
                  : "Bu firmaya henüz çalışan eklenmemiş."
            }
          />
        ) : (
          <>
            {/* Mobil */}
            <div className="sm:hidden divide-y divide-gray-100">
              {filteredUsers.map((user) => (
                <div key={user._id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-9 h-9 bg-orange-50 rounded-full flex items-center justify-center shrink-0">
                    <FaUser className="w-3.5 h-3.5 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{user.name} {user.lastName}</p>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                      {user.email && <span className="truncate">{user.email}</span>}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">Kota: {getPeriodSummary(user)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canEditQuota && (
                      <button
                        type="button"
                        onClick={() => setQuotaDrawerUser(user)}
                        className="p-2 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100"
                        title="Kota ayarları"
                      >
                        <FaCog className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canUnlink && (
                      <button
                        type="button"
                        onClick={() => setConfirmUnlink(user)}
                        className="p-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 border border-red-100"
                        title="Firmadan çıkar"
                      >
                        <FaUnlink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop */}
            <div className="hidden sm:block">
              <table className="w-full">
                <thead className="bg-gray-50/80 sticky top-0">
                  <tr>
                    {["Çalışan", "İletişim", "Alt Rol", "Periyot Kotası", "Kalan Kota", "Durum", ...((canUnlink || canEditQuota) ? ["İşlem"] : [])].map((h, i) => (
                      <th key={i} className={`px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 ${["İşlem"].includes(h) ? "text-center w-24" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-orange-50 rounded-full flex items-center justify-center shrink-0">
                            <FaUser className="w-3 h-3 text-orange-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">{user.name} {user.lastName}</p>
                            <p className="text-[11px] text-gray-400">ID: {user._id?.slice(-8)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {user.email && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <FaEnvelope className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                              <span className="truncate">{user.email}</span>
                            </div>
                          )}
                          {user.tel && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <FaPhone className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                              <span>{user.tel}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-orange-50 text-orange-700">
                          {user.subRole || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-600">{getPeriodSummary(user)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold text-gray-800">
                          {formatTL(user.quotaOption?.periodicWalletRemaining)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.isActive !== undefined ? (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                            user.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? "bg-green-500" : "bg-red-500"}`} />
                            {user.isActive ? "Aktif" : "Pasif"}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      {(canUnlink || canEditQuota) && (
                        <td className="px-4 py-3 text-center">
                          <div className="inline-flex items-center justify-center gap-1.5">
                            {canEditQuota && (
                              <button
                                type="button"
                                onClick={() => setQuotaDrawerUser(user)}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100 transition-colors"
                                title="Kota ayarları"
                              >
                                <FaCog className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canUnlink && (
                              <button
                                type="button"
                                onClick={() => setConfirmUnlink(user)}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 transition-colors"
                                title="Firmadan çıkar"
                              >
                                <FaUnlink className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Kota Ayarları Drawer */}
      <EmployeeQuotaDrawer
        open={!!quotaDrawerUser}
        onClose={() => setQuotaDrawerUser(null)}
        employee={quotaDrawerUser}
        onSave={handleSaveQuota}
        isLoading={updateLoading}
      />

      {/* Çalışan Ekleme Onay Modalı (Şablon Seçimi ile) */}
      {!!confirmLink && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[9999]" onClick={() => { setConfirmLink(null); setSelectedTemplateId(""); }} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 z-[10000] w-[90%] max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <FaPlus className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Çalışan Ekle</h3>
                <p className="text-sm text-gray-500">{confirmLink.name} {confirmLink.lastName}</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Bu kullanıcıyı firmaya bağlamak için bir çalışan şablonu seçmeniz gerekmektedir.
              Şablon, çalışanın alt rolünü ve kota ayarlarını belirler.
            </p>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                Çalışan Şablonu *
              </label>
              {employeeTemplates.length === 0 ? (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  Bu firma için çalışan şablonu tanımlanmamış. Önce "Çalışan Şablonları" sekmesinden şablon oluşturun.
                </p>
              ) : (
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                >
                  <option value="">Şablon seçin...</option>
                  {employeeTemplates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name} ({t.periodType === "daily" ? "Günlük" : t.periodType === "weekly" ? "Haftalık" : "Aylık"}: ₺{((Number(t.periodQuota) || 0) / 100).toFixed(2)})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setConfirmLink(null); setSelectedTemplateId(""); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => handleLinkUser(confirmLink._id)}
                disabled={linkLoading || !selectedTemplateId}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {linkLoading ? "Ekleniyor..." : "Ekle"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Çalışan Çıkarma Onay Modalı */}
      <ConfirmationModal
        open={!!confirmUnlink}
        onClose={() => setConfirmUnlink(null)}
        onConfirm={() => confirmUnlink && handleUnlinkUser(confirmUnlink._id)}
        title="Çalışanı Çıkar"
        message={confirmUnlink ? `${confirmUnlink.name} ${confirmUnlink.lastName} kullanıcısını firmadan çıkarmak istediğinize emin misiniz?` : ""}
        confirmText="Çıkar"
        cancelText="İptal"
        isLoading={unlinkLoading}
      />
    </div>
  );
};

export default EmployeesTab;
